import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  FileText,
  X,
  Check,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { HelpTooltip } from './ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const ALERTA_CONFIG = {
  fase_atrasada: { 
    icon: AlertTriangle, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  projeto_critico: { 
    icon: AlertTriangle, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  prazo_proximo: { 
    icon: Clock, 
    color: 'text-yellow-500', 
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  fase_concluida: { 
    icon: CheckCircle2, 
    color: 'text-green-500', 
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  diario_novo: { 
    icon: FileText, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  // Alertas de Estoque
  estoque_zerado: { 
    icon: AlertTriangle, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  estoque_baixo: { 
    icon: AlertTriangle, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  ponto_pedido: { 
    icon: Clock, 
    color: 'text-yellow-500', 
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  // Alertas CRM
  oportunidade_parada: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  proposta_sem_retorno: {
    icon: FileText,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  tarefa_vencida: {
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  tarefa_proxima: {
    icon: Clock,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200'
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const AlertasDropdown = () => {
  const navigate = useNavigate();
  const [alertas, setAlertas] = useState({ nao_lidos: [], lidos: [], total_nao_lidos: 0 });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchAlertas = useCallback(async () => {
    try {
      const response = await api.get('/alertas');
      setAlertas(response.data);
    } catch (error) {
      console.error('Erro ao buscar alertas:', error);
    }
  }, []);

  const verificarAlertas = async () => {
    setLoading(true);
    try {
      const response = await api.post('/alertas/verificar', {});
      if (response.data.alertas_gerados > 0) {
        toast.success(`${response.data.alertas_gerados} novo(s) alerta(s) gerado(s)`);
      } else {
        toast.info('Nenhum novo alerta');
      }
      await fetchAlertas();
    } catch (error) {
      toast.error('Erro ao verificar alertas');
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLido = async (alertaId, e) => {
    e?.stopPropagation();
    try {
      await api.put(`/alertas/${alertaId}/lido`, {});
      setAlertas(prev => ({
        ...prev,
        nao_lidos: prev.nao_lidos.filter(a => a.id !== alertaId),
        lidos: [prev.nao_lidos.find(a => a.id === alertaId), ...prev.lidos].filter(Boolean).slice(0, 20),
        total_nao_lidos: prev.total_nao_lidos - 1
      }));
    } catch (error) {
      console.error('Erro ao marcar alerta como lido:', error);
    }
  };

  const marcarTodosComoLidos = async () => {
    try {
      await api.put('/alertas/marcar-todos-lidos', {});
      setAlertas(prev => ({
        ...prev,
        lidos: [...prev.nao_lidos, ...prev.lidos].slice(0, 20),
        nao_lidos: [],
        total_nao_lidos: 0
      }));
      toast.success('Todos os alertas marcados como lidos');
    } catch (error) {
      toast.error('Erro ao marcar alertas como lidos');
    }
  };

  const deletarAlerta = async (alertaId, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/alertas/${alertaId}`);
      setAlertas(prev => ({
        ...prev,
        nao_lidos: prev.nao_lidos.filter(a => a.id !== alertaId),
        lidos: prev.lidos.filter(a => a.id !== alertaId),
        total_nao_lidos: prev.nao_lidos.some(a => a.id === alertaId) ? prev.total_nao_lidos - 1 : prev.total_nao_lidos
      }));
    } catch (error) {
      console.error('Erro ao deletar alerta:', error);
    }
  };

  const handleAlertaClick = (alerta) => {
    // Definir destino baseado no tipo do alerta
    let destino = null;
    
    if (alerta.tipo === 'fase_atrasada' || alerta.tipo === 'prazo_proximo' || alerta.tipo === 'fase_concluida') {
      destino = '/production';
    } else if (alerta.tipo === 'projeto_critico') {
      destino = '/projects';
    } else if (alerta.tipo === 'diario_novo') {
      destino = '/production';
    } else if (alerta.tipo === 'estoque_zerado' || alerta.tipo === 'estoque_baixo' || alerta.tipo === 'ponto_pedido') {
      destino = '/produtos';
    } else if (alerta.tipo === 'oportunidade_parada') {
      destino = '/crm/oportunidades';
    } else if (alerta.tipo === 'proposta_sem_retorno') {
      destino = '/crm/propostas';
    } else if (alerta.tipo === 'tarefa_vencida' || alerta.tipo === 'tarefa_proxima') {
      destino = '/crm/tarefas';
    } else if (alerta.tipo === 'cotacao_resposta_responsavel' || alerta.tipo === 'cotacao_aprovacao_pendente') {
      destino = alerta.referencia_id ? `/compras?cotacao_id=${alerta.referencia_id}` : '/compras';
    } else {
      // Tipo desconhecido - ir para cronograma como fallback
      destino = '/cronograma';
    }
    
    // Navegar para o destino
    if (destino) {
      navigate(destino);
    }
    
    // Marcar como lido se não estiver
    if (!alerta.lido) {
      marcarComoLido(alerta.id);
    }
    
    setIsOpen(false);
  };

  useEffect(() => {
    fetchAlertas();
    // Atualizar a cada 60 segundos
    const interval = setInterval(fetchAlertas, 60000);
    return () => clearInterval(interval);
  }, [fetchAlertas]);

  const renderAlerta = (alerta, isLido = false) => {
    const config = ALERTA_CONFIG[alerta.tipo] || ALERTA_CONFIG.diario_novo;
    const Icon = config.icon;
    
    return (
      <div
        key={alerta.id}
        className={`p-3 rounded-lg border cursor-pointer transition-colors ${config.bgColor} ${config.borderColor} ${isLido ? 'opacity-60' : ''} hover:opacity-80`}
        onClick={() => handleAlertaClick(alerta)}
      >
        <div className="flex items-start gap-3">
          <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{alerta.titulo}</p>
            <p className="text-xs text-gray-600 mt-0.5">{alerta.mensagem}</p>
            {alerta.projeto_nome && (
              <p className="text-xs text-gray-400 mt-1">📁 {alerta.projeto_nome}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">{formatDate(alerta.created_at)}</p>
          </div>
          <div className="flex items-center gap-1">
            {!isLido && (
              <button
                onClick={(e) => marcarComoLido(alerta.id, e)}
                className="p-1 hover:bg-white rounded"
                title="Marcar como lido"
              >
                <Check className="h-4 w-4 text-gray-400 hover:text-green-500" />
              </button>
            )}
            <button
              onClick={(e) => deletarAlerta(alerta.id, e)}
              className="p-1 hover:bg-white rounded"
              title="Remover"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {alertas.total_nao_lidos > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
            >
              {alertas.total_nao_lidos > 9 ? '9+' : alertas.total_nao_lidos}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span>Alertas</span>
            <HelpTooltip 
              title="Central de Alertas"
              description="Receba notificações automáticas sobre fases atrasadas, prazos próximos e atualizações importantes. Use 'Verificar' para buscar novos alertas manualmente."
              connections={['Dashboard', 'Produção', 'Cronograma']}
              iconSize={14}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={verificarAlertas}
              disabled={loading}
              className="h-7 text-xs"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Verificar
            </Button>
            {alertas.total_nao_lidos > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={marcarTodosComoLidos}
                className="h-7 text-xs"
              >
                Marcar todos lidos
              </Button>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-[400px]">
          {alertas.nao_lidos.length === 0 && alertas.lidos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Nenhum alerta</p>
              <p className="text-xs mt-1">Clique em Verificar para buscar novos alertas</p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {alertas.nao_lidos.length > 0 && (
                <>
                  <p className="text-xs font-medium text-gray-500 px-1">Novos ({alertas.nao_lidos.length})</p>
                  {alertas.nao_lidos.map(alerta => renderAlerta(alerta, false))}
                </>
              )}
              
              {alertas.lidos.length > 0 && (
                <>
                  <p className="text-xs font-medium text-gray-500 px-1 mt-4">Anteriores</p>
                  {alertas.lidos.map(alerta => renderAlerta(alerta, true))}
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AlertasDropdown;
